import { useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import type { LlmChatSessionDto } from '@paladin-monorepo/common';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  sidebarCollapseChevronClassName,
  sidebarCollapseLabelClassName,
  useSidebar,
} from '@/components/ui/sidebar';
import { SidebarChevronIcon, SidebarMonomoAiMarkIcon } from '@/components/sidebar/sidebar-icons';
import { cn } from '@/lib/utils';
import { llmChatApi } from '@/lib/api/llm-chat';
import { llmChatSessionsQueryKey } from '@/lib/llm-chat-sessions-query';

function sortSessionsByUpdatedAtDesc(sessions: LlmChatSessionDto[]): LlmChatSessionDto[] {
  return [...sessions].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <li className="list-none px-2 pt-2 first:pt-0">
      <span className="font-label text-[11px] uppercase tracking-wide text-white/50">{children}</span>
    </li>
  );
}

export function NavMonomoAi() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { sessionId: activeSessionId } = useParams<{ sessionId?: string }>();
  const { state, isMobile } = useSidebar();
  const goToMainOnParentClick = state === 'collapsed' && !isMobile;

  const isMonomoAiRoute = pathname === '/monomo-ai' || pathname.startsWith('/monomo-ai/');
  const isChatRoute = pathname.startsWith('/llm-chat/');
  const defaultOpen = isMonomoAiRoute || isChatRoute;

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: llmChatSessionsQueryKey(),
    queryFn: () => llmChatApi.getSessions(),
  });

  const { pinnedSessions, otherSessions } = useMemo(() => {
    const pinned = sortSessionsByUpdatedAtDesc(sessions.filter((session) => session.pinned));
    const other = sortSessionsByUpdatedAtDesc(sessions.filter((session) => !session.pinned));
    return { pinnedSessions: pinned, otherSessions: other };
  }, [sessions]);

  const parentActive = isMonomoAiRoute || isChatRoute;
  const agentsActive = pathname === '/monomo-ai';

  const buttonClassName = cn(
    'h-[35px] rounded-md font-label text-white hover:bg-white/10 hover:text-white',
    'data-[active=true]:bg-white data-[active=true]:font-medium data-[active=true]:text-brand-secondary',
    'data-[active=true]:hover:bg-white data-[active=true]:hover:text-brand-secondary',
    'group-data-[collapsible=icon]:size-[35px]! group-data-[collapsible=icon]:p-2!',
  );

  const subButtonClassName =
    'bg-transparent font-label text-white/80 hover:bg-white/10 hover:text-white active:bg-white/10 data-[active=true]:bg-white/15 data-[active=true]:text-white';

  const label = (
    <>
      <SidebarMonomoAiMarkIcon className="size-5 shrink-0" />
      <span className={cn('flex-1 truncate', sidebarCollapseLabelClassName)}>
        {t('sidebar.monomoAi')}
      </span>
    </>
  );

  const renderSessionLink = (session: LlmChatSessionDto) => {
    const title = session.title?.trim() || t('llmChat.untitledSession');
    const isActive = activeSessionId === session.id;

    return (
      <SidebarMenuSubItem key={session.id}>
        <SidebarMenuSubButton asChild isActive={isActive} className={subButtonClassName}>
          <Link to={`/llm-chat/${session.id}`} title={title}>
            <span className="truncate">{title}</span>
          </Link>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  };

  return (
    <SidebarGroup className="p-2 pt-0">
      <SidebarMenu className="gap-1.5">
        <Collapsible asChild defaultOpen={defaultOpen} className="group/collapsible">
          <SidebarMenuItem className="group/menu-item">
            <SidebarMenuButton
              asChild
              tooltip={t('sidebar.monomoAi')}
              isActive={parentActive}
              className={buttonClassName}
            >
              {goToMainOnParentClick ? (
                <Link
                  to="/monomo-ai"
                  className="flex w-full items-center gap-2.5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0"
                >
                  {label}
                  <SidebarChevronIcon
                    className={cn(
                      sidebarCollapseChevronClassName,
                      'text-current opacity-80',
                    )}
                  />
                </Link>
              ) : (
                <CollapsibleTrigger>
                  {label}
                  <SidebarChevronIcon
                    className={cn(
                      sidebarCollapseChevronClassName,
                      'text-current opacity-80 transition-transform',
                      'group-data-[state=open]/collapsible:rotate-90',
                    )}
                  />
                </CollapsibleTrigger>
              )}
            </SidebarMenuButton>
            <CollapsibleContent>
              <SidebarMenuSub className="mx-0 border-l border-white/20 px-2">
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton asChild isActive={agentsActive} className={subButtonClassName}>
                    <Link to="/monomo-ai">
                      <span>{t('sidebar.monomoAiSections.agents')}</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>

                {isLoading ? (
                  <li className="list-none px-2 py-1">
                    <Loader2 className="h-4 w-4 animate-spin text-white/50" />
                  </li>
                ) : (
                  <>
                    <SectionLabel>{t('sidebar.monomoAiSections.pinned')}</SectionLabel>
                    {pinnedSessions.length === 0 ? (
                      null
                      // <li className="list-none px-2 pb-1">
                      //   <span className="font-label text-xs text-white/40">{t('llmChat.noSessions')}</span>
                      // </li>
                    ) : (
                      pinnedSessions.map(renderSessionLink)
                    )}

                    <SectionLabel>{t('sidebar.monomoAiSections.chats')}</SectionLabel>
                    {otherSessions.length === 0 ? (
                      <li className="list-none px-2 pb-1">
                        <span className="font-label text-xs text-white/40">{t('llmChat.noSessions')}</span>
                      </li>
                    ) : (
                      otherSessions.map(renderSessionLink)
                    )}
                  </>
                )}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      </SidebarMenu>
    </SidebarGroup>
  );
}
